// src/pages/ProvisioningQRPage.jsx  — COMPLETE UPDATED FILE
// Changes: ApplicationDropdown added, app select → auto-fill appName + appPackage

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { provisioningQRController } from '../../controllers/provisioningQRController';
import CompanyDropdown     from '../../components/CompanyDropdown';
import ApplicationDropdown from '../../components/ApplicationDropdown';   // ✅ NEW
import {
  Plus, Search, Download, Eye, Edit2, Trash2, X, AlertCircle,
  RefreshCw, QrCode, Smartphone, Users, Shield, Clock, CheckCircle2,
  XCircle, Copy, ChevronDown, ChevronUp, ToggleLeft, ToggleRight,
  Building2, Package, Calendar, Infinity, Hash, Zap, Filter,
  MoreVertical, ArrowRight, AlertTriangle,
} from 'lucide-react';

// ─── styles (unchanged) ──────────────────────────────────────────────────────
const S = {
  page:       { padding:0, fontFamily:"'DM Sans', system-ui, sans-serif" },
  card:       { background:'#fff', borderRadius:14, border:'1px solid #e8ecf0', overflow:'hidden' },
  modal:      { position:'fixed', inset:0, background:'rgba(15,23,42,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20, backdropFilter:'blur(4px)' },
  modalBox:   { background:'#fff', borderRadius:16, width:'100%', maxHeight:'92vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 32px 80px rgba(0,0,0,0.25)' },
  modalHead:  { padding:'20px 24px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 },
  modalFoot:  { padding:'16px 24px', borderTop:'1px solid #f1f5f9', display:'flex', justifyContent:'flex-end', gap:10, flexShrink:0, background:'#fafbfc' },
  scrollBody: { padding:24, overflowY:'auto', flex:1 },
  btnPri:     { display:'inline-flex', alignItems:'center', gap:7, padding:'10px 20px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'#fff', border:'none', borderRadius:9, fontSize:13, fontWeight:600, cursor:'pointer', letterSpacing:'0.01em' },
  btnSec:     { display:'inline-flex', alignItems:'center', gap:7, padding:'10px 20px', background:'#fff', color:'#374151', border:'1px solid #d1d5db', borderRadius:9, fontSize:13, fontWeight:500, cursor:'pointer' },
  btnGhost:   { display:'inline-flex', alignItems:'center', gap:6, padding:'7px 12px', background:'transparent', border:'none', borderRadius:7, cursor:'pointer', fontSize:12, fontWeight:500, color:'#64748b' },
  label:      { display:'block', fontSize:12, fontWeight:700, color:'#374151', marginBottom:6, textTransform:'uppercase', letterSpacing:'0.06em' },
  input:      { width:'100%', padding:'10px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:14, outline:'none', color:'#1e293b', background:'#fff', boxSizing:'border-box', transition:'border-color .15s' },
  textarea:   { width:'100%', padding:'10px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:14, outline:'none', color:'#1e293b', background:'#fff', boxSizing:'border-box', minHeight:72, resize:'vertical', fontFamily:'inherit' },
  select:     { width:'100%', padding:'10px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:14, outline:'none', color:'#1e293b', background:'#fff', cursor:'pointer', boxSizing:'border-box' },
  table:      { width:'100%', borderCollapse:'collapse' },
  th:         { padding:'13px 16px', textAlign:'left', fontSize:11, fontWeight:700, color:'#94a3b8', borderBottom:'1px solid #f1f5f9', background:'#fafbfc', textTransform:'uppercase', letterSpacing:'0.07em' },
  td:         { padding:'14px 16px', borderBottom:'1px solid #f8fafc', fontSize:13, verticalAlign:'middle' },
  center:     { padding:48, textAlign:'center', color:'#94a3b8', fontSize:14 },
  errBox:     { background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', marginBottom:18 },
  sectionTitle: { fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:14, display:'flex', alignItems:'center', gap:6 },
  divider:    { height:1, background:'#f1f5f9', margin:'20px 0' },
};

// ─── reusable atoms (unchanged) ──────────────────────────────────────────────
const Badge = ({ color, bg, children }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, color, background:bg, letterSpacing:'0.02em' }}>{children}</span>
);
const StatusBadge = ({ status }) => {
  const m = { Active:{color:'#166534',bg:'#dcfce7',dot:'#16a34a'}, Expired:{color:'#7c2d12',bg:'#ffedd5',dot:'#ea580c'}, 'Limit Reached':{color:'#7e22ce',bg:'#f3e8ff',dot:'#9333ea'}, Inactive:{color:'#475569',bg:'#f1f5f9',dot:'#94a3b8'} };
  const s = m[status] || m.Inactive;
  return <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:20, fontSize:11, fontWeight:700, color:s.color, background:s.bg }}><span style={{ width:6, height:6, borderRadius:'50%', background:s.dot, display:'inline-block' }}/>{status}</span>;
};
const TypeBadge = ({ type }) => {
  const m = { new_device_single:{label:'New Device',color:'#1e40af',bg:'#dbeafe',Icon:Smartphone}, running_single:{label:'Running Device',color:'#065f46',bg:'#d1fae5',Icon:Zap}, new_device_group:{label:'Group Enrollment',color:'#6b21a8',bg:'#f3e8ff',Icon:Users} };
  const t = m[type] || { label:type, color:'#374151', bg:'#f3f4f6', Icon:QrCode };
  return <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:7, fontSize:11, fontWeight:600, color:t.color, background:t.bg }}><t.Icon size={11}/>{t.label}</span>;
};
const UsageBar = ({ current, limit }) => {
  if (limit===null||limit===undefined) return <span style={{ fontSize:12, color:'#94a3b8', display:'flex', alignItems:'center', gap:4 }}><Infinity size={13}/> Unlimited</span>;
  const pct = Math.min(100, Math.round((current/limit)*100));
  const color = pct>=100?'#ef4444':pct>=80?'#f59e0b':'#10b981';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:4 }}>
        <span style={{ color:'#64748b' }}>{current} / {limit} devices</span>
        <span style={{ color, fontWeight:700 }}>{pct}%</span>
      </div>
      <div style={{ height:5, background:'#f1f5f9', borderRadius:3, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:3, transition:'width .4s' }}/>
      </div>
    </div>
  );
};
const FormField = ({ label, children, hint, required }) => (
  <div>
    <div style={S.label}>{label}{required && <span style={{ color:'#ef4444', marginLeft:3 }}>*</span>}</div>
    {children}
    {hint && <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>{hint}</div>}
  </div>
);
const ErrList = ({ errors }) => !errors?.length ? null : (
  <div style={S.errBox}>
    {errors.map((e,i) => (
      <div key={i} style={{ display:'flex', alignItems:'center', gap:8, color:'#dc2626', fontSize:13, marginBottom:i<errors.length-1?4:0 }}>
        <AlertCircle size={13}/>{e}
      </div>
    ))}
  </div>
);

// ─── QR Display Card (unchanged) ─────────────────────────────────────────────
const QRDisplayCard = ({ qr, onClose, onDownload, onRegenerate, onCopy }) => (
  <div style={S.modal} onClick={onClose}>
    <div style={{ ...S.modalBox, maxWidth:480 }} onClick={e=>e.stopPropagation()}>
      <div style={S.modalHead}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <QrCode size={18} color="#fff"/>
          </div>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:'#1e293b' }}>{qr.name}</div>
            <div style={{ fontSize:12, color:'#64748b', marginTop:1 }}><TypeBadge type={qr.type}/></div>
          </div>
        </div>
        <button style={S.btnGhost} onClick={onClose}><X size={18}/></button>
      </div>
      <div style={{ ...S.scrollBody, textAlign:'center' }}>
        <div style={{ display:'inline-block', padding:16, background:'#fff', borderRadius:14, border:'2px solid #f1f5f9', marginBottom:20, boxShadow:'0 4px 20px rgba(0,0,0,0.08)' }}>
          {qr.qrImageUrl
            ? <img src={qr.qrImageUrl} alt="QR Code" style={{ width:220, height:220, display:'block' }}/>
            : <div style={{ width:220, height:220, background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', borderRadius:8 }}>
                <div style={{ textAlign:'center', color:'#94a3b8' }}><QrCode size={48} style={{ marginBottom:8 }}/><div style={{ fontSize:12 }}>QR Not Generated</div></div>
              </div>
          }
        </div>
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:20, flexWrap:'wrap' }}>
          <StatusBadge status={qr.status}/>
          <Badge color="#1e40af" bg="#dbeafe">{qr.appName}</Badge>
          {qr.companyName && <Badge color="#065f46" bg="#d1fae5">{qr.companyName}</Badge>}
          {qr.type==='new_device_group' && qr.groupName && <Badge color="#6b21a8" bg="#f3e8ff">{qr.groupName}</Badge>}
        </div>
        <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px 16px', textAlign:'left', marginBottom:16 }}>
          {[
            ['App Package', qr.appPackage,  Package],
            ['Enrolled',    qr.deviceLimit!==null?`${qr.enrolledCount} / ${qr.deviceLimit}`:`${qr.enrolledCount} / ∞`, Smartphone],
            ['Expires',     qr.expiresAt?new Date(qr.expiresAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'Never', Calendar],
            ['Token',       (qr.qrToken?.slice(0,16)||'')+'...', Shield],
          ].map(([k,v,Icon]) => (
            <div key={k} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #f1f5f9', fontSize:12 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, color:'#64748b' }}><Icon size={12}/>{k}</div>
              <span style={{ fontWeight:600, color:'#1e293b', fontFamily:k==='Token'||k==='App Package'?'monospace':'inherit', fontSize:k==='Token'||k==='App Package'?11:12 }}>{v}</span>
            </div>
          ))}
        </div>
        {qr.deviceLimit!==null && <div style={{ marginBottom:16 }}><UsageBar current={qr.enrolledCount} limit={qr.deviceLimit}/></div>}
        <div style={{ background:'linear-gradient(135deg,#eff6ff,#dbeafe)', borderRadius:10, padding:'12px 16px', fontSize:12, color:'#1e40af', fontWeight:500, display:'flex', alignItems:'center', gap:8 }}>
          <Smartphone size={14}/>
          {qr.type==='new_device_single'?'On new Android device: Power on → tap 6 times on welcome screen → scan this QR'
            :qr.type==='running_single'?'On enrolled device: Open app → Settings → Re-enroll → Scan this QR'
            :qr.type==='new_device_group'?'Send to group members for bulk enrollment'
            :'Send to group members for bulk enrollment'}
        </div>
      </div>
      <div style={S.modalFoot}>
        <button style={S.btnGhost} onClick={()=>onCopy(qr.qrToken)}><Copy size={14}/> Copy Token</button>
        <button style={S.btnGhost} onClick={()=>onRegenerate(qr.id)}><RefreshCw size={14}/> Regenerate</button>
        <button style={{ ...S.btnPri, background:'linear-gradient(135deg,#10b981,#059669)' }} onClick={()=>onDownload(qr.id,`qr-${qr.name}.png`)}>
          <Download size={14}/> Download QR
        </button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// QRFormModal  ✅ APPLICATION DROPDOWN INTEGRATED
// ─────────────────────────────────────────────────────────────────────────────
const QRFormModal = ({ mode, initialData, onClose, onSuccess, groups=[] }) => {
  const [formData, setFormData] = useState({
    name:        initialData?.name        || '',
    description: initialData?.description || '',
    type:        initialData?.type        || 'new_device_single',
    companyId:   initialData?.companyId   || '',
    appName:     initialData?.appName     || '',
    appPackage:  initialData?.appPackage  || '',
    groupId:     initialData?.groupId     || '',
    hasLimit:    initialData?.deviceLimit !== null && initialData?.deviceLimit !== undefined,
    deviceLimit: initialData?.deviceLimit || '',
    hasExpiry:   Boolean(initialData?.expiresAt),
    expiresAt:   initialData?.expiresAt ? new Date(initialData.expiresAt).toISOString().split('T')[0] : '',
    isActive:    initialData?.isActive !== undefined ? initialData.isActive : true,
  });

  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setFormData(p => ({ ...p, [k]: v }));

  // ── Company dropdown ───────────────────────────────────────────────────────
  const handleCompanyChange = (company) => {
    set('companyId', company ? company.id : '');
  };

  // ── ✅ Application dropdown — auto-fill appName + appPackage ───────────────
  const handleAppChange = (app) => {
    if (app) {
      set('appName',    app.appName);
      set('appPackage', app.appPackage);
    } else {
      set('appName',    '');
      set('appPackage', '');
    }
  };

  const handleSubmit = async () => {
    setErrors([]);
    const errs = [];
    if (!formData.name)       errs.push('QR name is required');
    if (!formData.companyId)  errs.push('Please select a company');
    if (!formData.appPackage) errs.push('Please select an application');
    if (formData.type==='new_device_group' && !formData.groupId) errs.push('Please select a group');
    if (formData.hasLimit && !formData.deviceLimit)   errs.push('Enter a device limit or disable it');
    if (formData.hasExpiry && !formData.expiresAt)    errs.push('Enter an expiry date or disable it');
    if (errs.length) { setErrors(errs); return; }

    const payload = {
      ...formData,
      deviceLimit: formData.hasLimit  ? Number(formData.deviceLimit) : null,
      expiresAt:   formData.hasExpiry ? formData.expiresAt            : null,
      groupId:     formData.type==='new_device_group' ? formData.groupId         : null,
    };
    setSaving(true);
    const res = mode==='create'
      ? await provisioningQRController.create(payload)
      : await provisioningQRController.update(initialData.id, payload);
    setSaving(false);
    if (res.success) { onSuccess(res); onClose(); }
    else setErrors(res.errors?.length ? res.errors : [res.message || 'Failed to save']);
  };

  const QR_TYPES = [
    { id:'new_device_single', label:'New Device',      desc:'Fresh enrollment via Hello screen', Icon:Smartphone },
    { id:'running_single',    label:'Running Device',   desc:'Re-enroll already-setup device',    Icon:Zap },
    { id:'new_device_group',      label:'Group Enrollment', desc:'Bulk enrollment for a user group',  Icon:Users },
  ];

  return (
    <div style={S.modal} onClick={()=>!saving&&onClose()}>
      <div style={{ ...S.modalBox, maxWidth:660 }} onClick={e=>e.stopPropagation()}>
        <div style={S.modalHead}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <QrCode size={18} color="#fff"/>
            </div>
            <div>
              <h2 style={{ fontSize:17, fontWeight:700, color:'#1e293b', margin:0 }}>
                {mode==='create' ? 'Generate New QR Code' : 'Edit QR Code'}
              </h2>
              <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>Android Device Provisioning</div>
            </div>
          </div>
          {!saving && <button style={S.btnGhost} onClick={onClose}><X size={18}/></button>}
        </div>

        <div style={S.scrollBody}>
          <ErrList errors={errors}/>

          {/* ── Basic Info ── */}
          <div style={S.sectionTitle}><Hash size={12}/> Basic Information</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
            <FormField label="QR Name" required>
              <input style={S.input} value={formData.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Field Team Batch 1"/>
            </FormField>
            <FormField label="Company" required>
              <CompanyDropdown
                value={formData.companyId}
                onChange={handleCompanyChange}
                placeholder="Search & select company..."
              />
            </FormField>
            <div style={{ gridColumn:'1 / -1' }}>
              <FormField label="Description">
                <textarea style={S.textarea} value={formData.description} onChange={e=>set('description',e.target.value)} placeholder="Optional: purpose of this QR code..."/>
              </FormField>
            </div>
          </div>

          <div style={S.divider}/>

          {/* ── QR Type ── */}
          <div style={S.sectionTitle}><QrCode size={12}/> QR Code Type</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
            {QR_TYPES.map(t => (
              <div key={t.id}
                style={{ padding:'14px 12px', borderRadius:10, cursor:'pointer', transition:'all .15s', border:`2px solid ${formData.type===t.id?'#3b82f6':'#e2e8f0'}`, background:formData.type===t.id?'#eff6ff':'#fafbfc', textAlign:'center' }}
                onClick={()=>set('type',t.id)}>
                <div style={{ width:36, height:36, borderRadius:8, background:formData.type===t.id?'#3b82f6':'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 8px' }}>
                  <t.Icon size={18} color={formData.type===t.id?'#fff':'#94a3b8'}/>
                </div>
                <div style={{ fontWeight:700, fontSize:12, color:formData.type===t.id?'#1d4ed8':'#374151', marginBottom:3 }}>{t.label}</div>
                <div style={{ fontSize:11, color:'#94a3b8', lineHeight:1.3 }}>{t.desc}</div>
              </div>
            ))}
          </div>

          {formData.type==='new_device_group' && (
            <div style={{ marginBottom:20 }}>
              <FormField label="Select Group" required>
                <select style={S.select} value={formData.groupId} onChange={e=>set('groupId',e.target.value)}>
                  <option value="">Select Group</option>
                  {groups.map(g=><option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </FormField>
            </div>
          )}

          <div style={S.divider}/>

          {/* ── ✅ App Configuration with ApplicationDropdown ── */}
          <div style={S.sectionTitle}><Package size={12}/> App Configuration</div>

          {/* Application search dropdown */}
          <div style={{ marginBottom:14 }}>
            <FormField label="Select Application" required hint="Select app — name and package will be filled automatically">
              <ApplicationDropdown
                value={formData.appPackage}
                onChange={handleAppChange}
                placeholder="Search & select application..."
              />
            </FormField>
          </div>

          {/* Auto-filled fields — still editable manually */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
            <FormField label="App Name" hint="Auto-filled, editable">
              <input
                style={{
                  ...S.input,
                  background: formData.appName ? '#f0fdf4' : '#fff',
                  borderColor: formData.appName ? '#86efac' : '#e2e8f0',
                }}
                value={formData.appName}
                onChange={e=>set('appName',e.target.value)}
                placeholder="e.g. Tracklio"
              />
            </FormField>
            <FormField label="App Package" required hint="Auto-filled, editable">
              <input
                style={{
                  ...S.input,
                  fontFamily:'monospace', fontSize:12,
                  background: formData.appPackage ? '#f0fdf4' : '#fff',
                  borderColor: formData.appPackage ? '#86efac' : '#e2e8f0',
                }}
                value={formData.appPackage}
                onChange={e=>set('appPackage',e.target.value)}
                placeholder="com.company.appname"
              />
            </FormField>
          </div>

          <div style={S.divider}/>

          {/* ── Limits & Expiry (unchanged) ── */}
          <div style={S.sectionTitle}><Shield size={12}/> Usage Limits & Expiry</div>

          <div style={{ background:'#fafbfc', borderRadius:10, border:'1px solid #f1f5f9', padding:'14px 16px', marginBottom:12 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:formData.hasLimit?12:0 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13, color:'#1e293b' }}>Device Limit</div>
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>Max devices that can enroll with this QR</div>
              </div>
              <div style={{ width:44, height:24, borderRadius:12, background:formData.hasLimit?'#3b82f6':'#e2e8f0', cursor:'pointer', position:'relative', transition:'background .2s' }}
                onClick={()=>set('hasLimit',!formData.hasLimit)}>
                <div style={{ position:'absolute', top:2, left:formData.hasLimit?22:2, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left .2s', boxShadow:'0 1px 4px rgba(0,0,0,.2)' }}/>
              </div>
            </div>
            {formData.hasLimit && <input type="number" min="1" style={S.input} value={formData.deviceLimit} onChange={e=>set('deviceLimit',e.target.value)} placeholder="e.g. 50"/>}
            {!formData.hasLimit && <div style={{ fontSize:12, color:'#10b981', display:'flex', alignItems:'center', gap:5, marginTop:4 }}><Infinity size={13}/> Unlimited enrollments</div>}
          </div>

          <div style={{ background:'#fafbfc', borderRadius:10, border:'1px solid #f1f5f9', padding:'14px 16px', marginBottom:20 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:formData.hasExpiry?12:0 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13, color:'#1e293b' }}>Expiry Date</div>
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>QR code will stop working after this date</div>
              </div>
              <div style={{ width:44, height:24, borderRadius:12, background:formData.hasExpiry?'#3b82f6':'#e2e8f0', cursor:'pointer', position:'relative', transition:'background .2s' }}
                onClick={()=>set('hasExpiry',!formData.hasExpiry)}>
                <div style={{ position:'absolute', top:2, left:formData.hasExpiry?22:2, width:20, height:20, borderRadius:'50%', background:'#fff', transition:'left .2s', boxShadow:'0 1px 4px rgba(0,0,0,.2)' }}/>
              </div>
            </div>
            {formData.hasExpiry && <input type="date" style={S.input} value={formData.expiresAt} onChange={e=>set('expiresAt',e.target.value)} min={new Date().toISOString().split('T')[0]}/>}
            {!formData.hasExpiry && <div style={{ fontSize:12, color:'#10b981', display:'flex', alignItems:'center', gap:5, marginTop:4 }}><CheckCircle2 size={13}/> No expiry — QR stays active indefinitely</div>}
          </div>
        </div>

        <div style={S.modalFoot}>
          <button style={S.btnSec} disabled={saving} onClick={onClose}>Cancel</button>
          <button style={S.btnPri} disabled={saving} onClick={handleSubmit}>
            {saving
              ? <><RefreshCw size={14} style={{ animation:'spin 1s linear infinite' }}/> Generating...</>
              : <><QrCode size={14}/> {mode==='create'?'Generate QR Code':'Save Changes'}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── EnrollmentLogsModal (unchanged) ─────────────────────────────────────────
const EnrollmentLogsModal = ({ qr, onClose }) => {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await provisioningQRController.getEnrollments(qr.id);
      if (res.success) setLogs(res.data);
      setLoading(false);
    })();
  }, [qr.id]);
  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth:700 }} onClick={e=>e.stopPropagation()}>
        <div style={S.modalHead}>
          <div>
            <h2 style={{ fontSize:16, fontWeight:700, color:'#1e293b', margin:0 }}>Enrollment Log — {qr.name}</h2>
            <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>{qr.enrolledCount} devices enrolled</div>
          </div>
          <button style={S.btnGhost} onClick={onClose}><X size={18}/></button>
        </div>
        <div style={S.scrollBody}>
          {loading ? <div style={S.center}>Loading logs...</div>
          : logs.length===0 ? <div style={S.center}><Smartphone size={36} style={{ marginBottom:10, opacity:0.3 }}/><div>No devices enrolled yet</div></div>
          : (
            <table style={S.table}>
              <thead><tr>{['Device','Model','Enrolled At','IMEI / Serial','Status'].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
              <tbody>
                {logs.map((log,i)=>(
                  <tr key={i}>
                    <td style={S.td}><div style={{ fontWeight:600, color:'#1e293b' }}>{log.deviceName||'—'}</div></td>
                    <td style={S.td}><span style={{ fontSize:12, color:'#64748b' }}>{log.deviceModel||'—'}</span></td>
                    <td style={S.td}><span style={{ fontSize:12, color:'#64748b' }}>{log.enrolledAt?new Date(log.enrolledAt).toLocaleString('en-IN'):'—'}</span></td>
                    <td style={S.td}><span style={{ fontSize:11, fontFamily:'monospace', color:'#94a3b8' }}>{log.imei||log.serial||'—'}</span></td>
                    <td style={S.td}><Badge color="#166534" bg="#dcfce7">Enrolled</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={S.modalFoot}><button style={S.btnSec} onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
};

// ─── Main Page (unchanged) ───────────────────────────────────────────────────
const ProvisioningQRPage = ({ companyId, groups=[] }) => {
  const [qrList,      setQrList]      = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [filterType,  setFilterType]  = useState('All');
  const [filterStatus,setFStatus]     = useState('All');
  const [sortField,   setSortField]   = useState('createdAt');
  const [sortDir,     setSortDir]     = useState('desc');
  const [page,        setPage]        = useState(1);
  const PER_PAGE = 12;

  const [showCreate,  setShowCreate]  = useState(false);
  const [showEdit,    setShowEdit]    = useState(false);
  const [showQR,      setShowQR]      = useState(false);
  const [showLogs,    setShowLogs]    = useState(false);
  const [selectedQR,  setSelectedQR]  = useState(null);
  const [toast,       setToast]       = useState(null);

  const stats = { total:qrList.length, active:qrList.filter(q=>q.status==='Active').length, expired:qrList.filter(q=>q.status==='Expired').length, limitHit:qrList.filter(q=>q.status==='Limit Reached').length, enrolled:qrList.reduce((s,q)=>s+(q.enrolledCount||0),0) };

  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null),3200); };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const res = await provisioningQRController.getAll(companyId?{companyId}:{});
    if (res.success) setQrList(res.data); else showToast(res.message||'Failed to load','error');
    setLoading(false);
  }, [companyId]);

  useEffect(()=>{fetchAll();},[fetchAll]);

  const filtered = qrList
    .filter(q=>!search||q.name.toLowerCase().includes(search.toLowerCase())||q.appPackage?.toLowerCase().includes(search.toLowerCase())||q.companyName?.toLowerCase().includes(search.toLowerCase()))
    .filter(q=>filterType==='All'||q.type===filterType)
    .filter(q=>filterStatus==='All'||q.status===filterStatus)
    .sort((a,b)=>{const av=a[sortField],bv=b[sortField];if(typeof av==='string') return sortDir==='asc'?av.localeCompare(bv):bv.localeCompare(av);return sortDir==='asc'?(av||0)-(bv||0):(bv||0)-(av||0);});

  const paginated  = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);
  const totalPages = Math.ceil(filtered.length/PER_PAGE);
  const handleSort = f => { if(sortField===f) setSortDir(d=>d==='asc'?'desc':'asc'); else{setSortField(f);setSortDir('desc');} };
  const handleDelete  = async qr => { if(!window.confirm(`Delete "${qr.name}"?`))return; const res=await provisioningQRController.delete(qr.id); if(res.success){fetchAll();showToast('QR code deleted');}else showToast(res.message,'error'); };
  const handleToggle  = async qr => { const res=await provisioningQRController.toggle(qr.id); if(res.success){fetchAll();showToast(`QR ${res.data.isActive?'activated':'deactivated'}`);}else showToast(res.message,'error'); };
  const handleRegenerate = async id => { const res=await provisioningQRController.regenerate(id); if(res.success){fetchAll();if(selectedQR?.id===id)setSelectedQR(res.data);showToast('QR code regenerated');}else showToast(res.message,'error'); };
  const handleDownload   = async (id,filename) => { const res=await provisioningQRController.downloadQR(id,filename); if(!res.success)showToast(res.message,'error'); };
  const handleCopy = token => { navigator.clipboard?.writeText(token).then(()=>showToast('Token copied to clipboard')); };

  return (
    <div style={S.page}>
      {toast && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:9999, padding:'12px 20px', borderRadius:10, background:toast.type==='error'?'#fee2e2':'#f0fdf4', color:toast.type==='error'?'#dc2626':'#166534', fontWeight:600, fontSize:13, border:`1px solid ${toast.type==='error'?'#fecaca':'#bbf7d0'}`, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', display:'flex', alignItems:'center', gap:8 }}>
          {toast.type==='error'?<AlertCircle size={15}/>:<CheckCircle2 size={15}/>}{toast.msg}
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:26, fontWeight:800, color:'#0f172a', margin:0, letterSpacing:'-0.02em' }}>Device Provisioning QR</h1>
          <p style={{ fontSize:13, color:'#64748b', marginTop:4, marginBottom:0 }}>Generate Android zero-touch enrollment QR codes for mass deployment</p>
        </div>
        <button style={S.btnPri} onClick={()=>setShowCreate(true)}><Plus size={16}/> Generate QR Code</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:24 }}>
        {[['Total QRs',stats.total,'#3b82f6',QrCode,'#eff6ff'],['Active',stats.active,'#16a34a',CheckCircle2,'#f0fdf4'],['Expired',stats.expired,'#ea580c',Clock,'#fff7ed'],['Limit Reached',stats.limitHit,'#9333ea',AlertTriangle,'#faf5ff'],['Total Enrolled',stats.enrolled,'#0ea5e9',Smartphone,'#f0f9ff']].map(([label,val,color,Icon,bg])=>(
          <div key={label} style={{ ...S.card, padding:'16px 18px', background:bg, border:`1px solid ${color}18` }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
              <span style={{ fontSize:11, color:'#64748b', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</span>
              <Icon size={18} color={color}/>
            </div>
            <div style={{ fontSize:26, fontWeight:800, color }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ flex:1, minWidth:220, position:'relative' }}>
          <Search size={15} color="#94a3b8" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }}/>
          <input type="text" placeholder="Search by name, package, company..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} style={{ ...S.input, paddingLeft:36, fontSize:13 }}/>
        </div>
        <select value={filterType} onChange={e=>{setFilterType(e.target.value);setPage(1);}} style={{ ...S.select, width:'auto', fontSize:13, padding:'10px 14px' }}>
          <option value="All">All Types</option>
          <option value="new_device_single">New Device</option>
          <option value="running_single">Running Device</option>
          <option value="new_device_group">Group Enrollment</option>
        </select>
        <select value={filterStatus} onChange={e=>{setFStatus(e.target.value);setPage(1);}} style={{ ...S.select, width:'auto', fontSize:13, padding:'10px 14px' }}>
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Expired">Expired</option>
          <option value="Limit Reached">Limit Reached</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div style={S.card}>
        {loading ? (
          <div style={S.center}><RefreshCw size={28} style={{ animation:'spin 1s linear infinite', marginBottom:10 }}/><div>Loading QR codes...</div></div>
        ) : paginated.length===0 ? (
          <div style={S.center}>
            <QrCode size={48} style={{ marginBottom:12, opacity:0.2 }}/>
            <div style={{ fontSize:15, fontWeight:600, color:'#374151', marginBottom:4 }}>No QR Codes Found</div>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>Generate your first provisioning QR to get started</div>
            <button style={S.btnPri} onClick={()=>setShowCreate(true)}><Plus size={14}/> Generate QR Code</button>
          </div>
        ) : (
          <>
            <div style={{ overflowX:'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th} onClick={()=>handleSort('name')}><div style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer' }}>QR Name{sortField==='name'&&(sortDir==='asc'?<ChevronUp size={12}/>:<ChevronDown size={12}/>)}</div></th>
                    <th style={S.th}>Type</th>
                    <th style={S.th}>App / Package</th>
                    <th style={S.th}>Company</th>
                    <th style={S.th}>Usage</th>
                    <th style={S.th}>Expiry</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th} onClick={()=>handleSort('createdAt')}><div style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer' }}>Created{sortField==='createdAt'&&(sortDir==='asc'?<ChevronUp size={12}/>:<ChevronDown size={12}/>)}</div></th>
                    <th style={S.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map(qr=>(
                    <tr key={qr.id} style={{ cursor:'pointer', transition:'background .12s' }} onMouseEnter={e=>e.currentTarget.style.background='#fafbfc'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <td style={S.td} onClick={()=>{setSelectedQR(qr);setShowQR(true);}}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:40, height:40, borderRadius:8, background:qr.isUsable?'linear-gradient(135deg,#eff6ff,#dbeafe)':'#f8fafc', border:'1px solid #e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                            {qr.qrImageUrl?<img src={qr.qrImageUrl} alt="QR" style={{ width:32, height:32, borderRadius:4 }}/>:<QrCode size={18} color={qr.isUsable?'#3b82f6':'#cbd5e1'}/>}
                          </div>
                          <div>
                            <div style={{ fontWeight:700, fontSize:13, color:'#1e293b' }}>{qr.name}</div>
                            {qr.description&&<div style={{ fontSize:11, color:'#94a3b8', marginTop:1, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{qr.description}</div>}
                            {qr.type==='new_device_group'&&qr.groupName&&<div style={{ fontSize:11, color:'#7c3aed', marginTop:2, display:'flex', alignItems:'center', gap:3 }}><Users size={10}/>{qr.groupName}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={S.td}><TypeBadge type={qr.type}/></td>
                      <td style={S.td}>
                        <div style={{ fontWeight:600, fontSize:12, color:'#1e293b' }}>{qr.appName}</div>
                        <div style={{ fontSize:11, fontFamily:'monospace', color:'#94a3b8', marginTop:2 }}>{qr.appPackage}</div>
                      </td>
                      <td style={S.td}><div style={{ display:'flex', alignItems:'center', gap:6 }}><Building2 size={13} color="#94a3b8"/><span style={{ fontSize:12, color:'#374151' }}>{qr.companyName||'—'}</span></div></td>
                      <td style={{ ...S.td, minWidth:140 }}><UsageBar current={qr.enrolledCount} limit={qr.deviceLimit}/></td>
                      <td style={S.td}>
                        {qr.expiresAt?(
                          <div><div style={{ fontSize:12, fontWeight:600, color:qr.isExpired?'#ef4444':'#374151' }}>{new Date(qr.expiresAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'})}</div>{qr.isExpired&&<div style={{ fontSize:10, color:'#ef4444', marginTop:1 }}>Expired</div>}</div>
                        ):(
                          <span style={{ fontSize:12, color:'#10b981', display:'flex', alignItems:'center', gap:4 }}><Infinity size={12}/> Never</span>
                        )}
                      </td>
                      <td style={S.td}><StatusBadge status={qr.status}/></td>
                      <td style={S.td}><div style={{ fontSize:11, color:'#94a3b8' }}>{qr.createdAt?new Date(qr.createdAt).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}):'—'}</div></td>
                      <td style={S.td}>
                        <div style={{ display:'flex', gap:2 }}>
                          <button style={{ ...S.btnGhost, color:'#3b82f6' }} title="View QR"        onClick={e=>{e.stopPropagation();setSelectedQR(qr);setShowQR(true);}}><Eye size={15}/></button>
                          <button style={{ ...S.btnGhost, color:'#6366f1' }} title="Enrollment Log" onClick={e=>{e.stopPropagation();setSelectedQR(qr);setShowLogs(true);}}><Smartphone size={15}/></button>
                          <button style={{ ...S.btnGhost, color:'#374151' }} title="Edit"           onClick={e=>{e.stopPropagation();setSelectedQR(qr);setShowEdit(true);}}><Edit2 size={15}/></button>
                          <button style={{ ...S.btnGhost, color:qr.isActive?'#f59e0b':'#10b981' }} title={qr.isActive?'Deactivate':'Activate'} onClick={e=>{e.stopPropagation();handleToggle(qr);}}>
                            {qr.isActive?<ToggleRight size={15}/>:<ToggleLeft size={15}/>}
                          </button>
                          <button style={{ ...S.btnGhost, color:'#ef4444' }} title="Delete" onClick={e=>{e.stopPropagation();handleDelete(qr);}}><Trash2 size={15}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages>1&&(
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'14px 20px', borderTop:'1px solid #f8fafc' }}>
                <span style={{ fontSize:12, color:'#94a3b8' }}>Showing {(page-1)*PER_PAGE+1}–{Math.min(page*PER_PAGE,filtered.length)} of {filtered.length}</span>
                <div style={{ display:'flex', gap:6 }}>
                  <button style={{ ...S.btnSec, padding:'7px 14px', fontSize:12 }} disabled={page===1} onClick={()=>setPage(p=>p-1)}>Previous</button>
                  <span style={{ padding:'7px 14px', fontSize:12, color:'#64748b' }}>Page {page} of {totalPages}</span>
                  <button style={{ ...S.btnSec, padding:'7px 14px', fontSize:12 }} disabled={page===totalPages} onClick={()=>setPage(p=>p+1)}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showCreate&&<QRFormModal mode="create" groups={groups} onClose={()=>setShowCreate(false)} onSuccess={res=>{fetchAll();showToast(res.message||'QR generated!');}}/>}
      {showEdit&&selectedQR&&<QRFormModal mode="edit" initialData={selectedQR} groups={groups} onClose={()=>setShowEdit(false)} onSuccess={res=>{fetchAll();showToast(res.message||'QR updated!');}}/>}
      {showQR&&selectedQR&&<QRDisplayCard qr={selectedQR} onClose={()=>setShowQR(false)} onDownload={handleDownload} onRegenerate={handleRegenerate} onCopy={handleCopy}/>}
      {showLogs&&selectedQR&&<EnrollmentLogsModal qr={selectedQR} onClose={()=>setShowLogs(false)}/>}

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default ProvisioningQRPage;